import { useLayoutEffect, useRef } from "react";

/**
 * Keeps sibling columns visually aligned: for each value of `attr`, sets every
 * matching element's `min-height` (or `min-width`, see `dimension`) to the
 * largest natural size sharing that value.
 *
 * Flow: clear inline min-sizes → forced reflow (read `offsetHeight`) → measure → apply.
 * `requestAnimationFrame` batches with the browser's next paint after layout-affecting changes elsewhere.
 * Also observes each row element individually (not just the container) so a row growing without
 * changing the container's own box (e.g. async content arriving after mount) still triggers a re-sync.
 *
 * @param {boolean} enabled — when false, no observers or DOM writes (e.g. tests).
 * @param {string} attr — DOM attribute name used both to select row elements and to key them into rows.
 * @param {"height"|"width"} [dimension="height"] — which axis to measure and sync.
 * @returns {React.RefObject<HTMLDivElement|null>} attach to the columns container.
 */
export function useCrossColumnRowSync(enabled, attr, dimension = "height") {
  const ref = useRef(null);
  const styleProp = dimension === "width" ? "minWidth" : "minHeight";

  useLayoutEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const container = ref.current;
    if (!container) {
      return undefined;
    }

    const selector = `[${attr}]`;
    /* Elements we've written an inline min-size to. Cleanup clears these
       directly rather than re-querying `selector`: by the time cleanup runs the
       attribute may already be gone from the DOM (e.g. toggling off removes it),
       which would otherwise leave the stale min-sizes stuck in place. */
    const touched = new Set();
    const clearTouched = () => {
      touched.forEach((el) => {
        el.style[styleProp] = "";
      });
      touched.clear();
    };
    /* Rows individually observed so far. Content swaps (e.g. week navigation
       re-keying every day card) replace row elements wholesale rather than
       resizing them in place, so freshly-mounted rows need to be (re-)added
       here rather than assuming the initial observe() call below covers them
       for the lifetime of the effect. */
    const observedRows = new WeakSet();

    const sync = () => {
      const all = container.querySelectorAll(selector);
      clearTouched();
      all.forEach((el) => {
        el.style[styleProp] = "";
        if (!observedRows.has(el)) {
          observedRows.add(el);
          ro.observe(el);
        }
      });
      /* Read layout so cleared min-sizes are applied before measuring row sizes. */
      if (container.offsetHeight === undefined) {
        return;
      }

      /** @type {Map<number, Element[]>} */
      const byRow = new Map();
      all.forEach((el) => {
        if (!(el instanceof HTMLElement)) {
          return;
        }
        const row = Number(el.getAttribute(attr));
        if (Number.isNaN(row)) {
          return;
        }
        let list = byRow.get(row);
        if (!list) {
          list = [];
          byRow.set(row, list);
        }
        list.push(el);
      });

      for (const els of byRow.values()) {
        if (els.length < 2) {
          continue;
        }
        let max = 0;
        for (const el of els) {
          const rect = el.getBoundingClientRect();
          max = Math.max(max, dimension === "width" ? rect.width : rect.height);
        }
        if (max < 1) {
          continue;
        }
        const px = `${Math.ceil(max)}px`;
        for (const el of els) {
          el.style[styleProp] = px;
          touched.add(el);
        }
      }
    };

    let rafId = 0;
    const run = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(sync);
    };

    const ro = new ResizeObserver(run);
    ro.observe(container);
    run();

    /* This effect only re-runs when `enabled`/`attr`/`dimension` change, but week
       navigation (and similar content swaps) mounts brand-new row elements
       under new React keys instead of resizing the existing ones. Without
       this, the ResizeObserver above keeps watching detached nodes from the
       previous week and alignment silently stops recomputing even though
       `enabled` never toggled off. */
    const mo = new MutationObserver(run);
    mo.observe(container, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      mo.disconnect();
      clearTouched();
    };
  }, [enabled, attr, dimension, styleProp]);

  return ref;
}
